"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import NextLink from "next/link";
import Image from "next/image";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { nrsCrispTextSx, nrsPanelSx } from "./nrsPageStyles";
import { HOME_FAQS } from "../../lib/seo/structuredData";
import { HOME_H1 } from "../../lib/seo/siteMetadata";
import { trackDownloadPageCtaClick } from "../../lib/analytics";

// ---------------------------------------------------------------------------
// Keyframe animations
// ---------------------------------------------------------------------------

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%       { opacity: 1;    transform: scale(1.18); }
`;

const orbDrift = keyframes`
  0%   { transform: translate(0px,   0px)   scale(1); }
  33%  { transform: translate(40px,  -30px) scale(1.08); }
  66%  { transform: translate(-20px, 20px)  scale(0.96); }
  100% { transform: translate(0px,   0px)   scale(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200%  center; }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIMULATOR_CHIPS = ["Gazebo", "MuJoCo", "Webots", "Godot"];
const PHYSICAL_CHIPS = [
  "Research Robots",
  "Commercial Robots",
  "STEM Robots",
  "BCI",
  "IoT",
  "Microcontrollers",
];


// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------

type Feature = {
  number: string;
  title: string;
  body: string;
  label: string;
  color: string;
  bg: string;
  detail: {
    capabilities: string[];
    /** href for secondary "Explore X" CTA; use "/nrs/download" to hide secondary */
    moreHref: string;
  };
};

const FEATURES: Feature[] = [
  {
    number: "01",
    title: "3D Brain Visualizer",
    body: "Inspect every cortical area, neuron, and synapse of your AI model in real time. Not logs. A live 3D connectome that shows you exactly how your neural architecture behaves.",
    label: "Brain Visualizer",
    color: "#90caf9",
    bg: "/img/tools/brain-visualizer-bg.jpg",
    detail: {
      capabilities: [
        "Live 3D rendering of every cortical area and neuron",
        "Real-time synapse activity heatmaps while experiments run",
        "Navigate the full connectome without pausing training",
        "Pause, rewind, and inspect any moment in the training timeline",
        "Export cortical snapshots for documentation and sharing",
      ],
      moreHref: "/nrs/download",
    },
  },
  {
    number: "02",
    title: "Any robot, any simulator",
    body: "Start with a built-in simulator with no hardware required. When you're ready, connect to a physical robot or ROS2 system. Same brain model, zero code changes.",
    label: "Embodiment Explorer",
    color: "#f3bd00",
    bg: "/img/tools/embodiment-explorer-bg.jpg",
    detail: {
      capabilities: [
        "Start with a built-in simulator with no hardware required",
        `Physical categories: research, commercial, STEM, BCI, IoT, microcontrollers`,
        "Simulation environments: Gazebo, MuJoCo, Webots, Godot",
        "Switch embodiment without modifying your brain model",
        "Community-contributed embodiment configurations",
      ],
      moreHref: "/embodiments",
    },
  },
  {
    number: "03",
    title: "Train by demonstration",
    body: "Teach FEAGI with CSV, video, or live sensor streams, not labeled datasets and GPU clusters. Imitation learning that adapts on-device, in real time.",
    label: "FEAGI Trainer",
    color: "#90caf9",
    bg: "/img/tools/feagi-trainer-bg.jpg",
    detail: {
      capabilities: [
        "Train from CSV files, video recordings, or live sensor streams",
        "No labeled datasets or GPU clusters required",
        "Imitation learning from demonstration",
        "Supervised, unsupervised, and reinforcement learning modes",
        "On-device adaptation. Your data never leaves your machine",
      ],
      moreHref: "/nrs/download",
    },
  },
  {
    number: "04",
    title: "ROS2 connector",
    body: "Native bridge to the ROS2 ecosystem. Topic inspection, message routing, and bidirectional FEAGI integration with no custom glue code required.",
    label: "ROS2 Connector",
    color: "#b0bec5",
    bg: "/img/tools/ros-connector-bg.jpg",
    detail: {
      capabilities: [
        "Native ROS2 topic bridge with no custom middleware",
        "Real-time message inspection and routing",
        "Bidirectional FEAGI integration out of the box",
        "Supports standard and custom ROS2 message types",
        "Works alongside existing ROS2 deployments without reconfiguration",
      ],
      moreHref: "/nrs/download",
    },
  },
  {
    number: "05",
    title: "Perception pipeline",
    body: "Vision Lab gives you segmentation, object detection, pose estimation, and depth, all streamed directly into FEAGI cortical areas as sensory input.",
    label: "Vision Lab",
    color: "#4fc3f7",
    bg: "/img/tools/vision-lab-bg.jpg",
    detail: {
      capabilities: [
        "Object detection, segmentation, and classification",
        "Pose estimation and depth mapping",
        "Direct stream into FEAGI cortical sensory areas",
        "Configurable resolution, frame rate, and region of interest",
        "Supports camera input, video files, and synthetic data",
      ],
      moreHref: "/nrs/download",
    },
  },
  {
    number: "06",
    title: "Hey FEAGI",
    body: "An in-app AI assistant that understands the FEAGI architecture. Describe what you want your model to do in plain language and it maps it to cortical configuration.",
    label: "Hey FEAGI",
    color: "#90caf9",
    bg: "/img/tools/hey-feagi-bg.jpg",
    detail: {
      capabilities: [
        "Natural language to cortical configuration mapping",
        "Contextual suggestions based on your active experiment",
        "Explain any cortical area or synaptic parameter in plain English",
        "Embedded in-workspace so you never leave NRS to get help",
        "Powered by FEAGI domain knowledge, not a generic model",
      ],
      moreHref: "/nrs/download",
    },
  },
];

const STEPS: Array<{ step: string; title: string; body: string }> = [
  {
    step: "1",
    title: "Download and install",
    body: "Free for macOS and Windows. Runs on your machine. Sign in to start.",
  },
  {
    step: "2",
    title: "Pick your embodiment",
    body: "Choose from a diverse range of supported simulators and start immediately with no hardware needed. Swap to a physical robot, ROS2 system, or custom environment whenever you're ready.",
  },
  {
    step: "3",
    title: "Start with a community brain",
    body: "Browse the Brain Hub for ready-made neural architectures, load one into your embodiment, and start experimenting immediately with no training required to see results.",
  },
];

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Triggers when the element enters the viewport. Fires once. */
function useInView() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "-40px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

// ---------------------------------------------------------------------------
// Decorative sub-components
// ---------------------------------------------------------------------------

/** Drifting gradient orb — purely decorative, aria-hidden. */
function GradientOrb({
  color,
  size,
  top,
  left,
  delay,
  duration,
}: {
  color: string;
  size: number;
  top: string;
  left: string;
  delay: number;
  duration: number;
}) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top,
        left,
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        opacity: 0.18,
        filter: "blur(60px)",
        pointerEvents: "none",
        animation: `${orbDrift} ${duration}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        willChange: "transform",
      }}
    />
  );
}

/** Hero video — NRS landing screen demo. Falls back to the dot grid if video errors. */
function HeroVideo() {
  const [failed, setFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 2, md: 2.5 },
        overflow: "hidden",
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
        boxShadow: (theme) =>
          `0 0 0 1px ${alpha(theme.palette.primary.main, 0.07)}, 0 40px 100px ${alpha("#000", 0.6)}`,
        aspectRatio: "16 / 10",
        background: "#0d0e10",
        animation: `${fadeIn} 0.8s ease 0.5s both`,
      }}
    >
      {/* Fake title bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 34,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.82),
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          gap: 0.625,
          zIndex: 2,
        }}
      >
        {["#ff5f57", "#ffbd2e", "#28c841"].map((c) => (
          <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: c }} />
        ))}
        <Typography
          variant="caption"
          sx={{
            ml: 1,
            color: "text.secondary",
            fontFamily: "monospace",
            fontSize: "0.68rem",
            letterSpacing: "0.02em",
          }}
        >
          Neurorobotics Studio, live experiment
        </Typography>
      </Box>

      {/* Animated glow border */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: -1,
          borderRadius: "inherit",
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.5)} 0%, transparent 40%, transparent 60%, ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
          backgroundSize: "200% 200%",
          animation: `${gradientShift} 4s ease infinite`,
          opacity: 0.6,
          pointerEvents: "none",
          zIndex: 3,
          mixBlendMode: "screen",
        }}
      />

      {!failed ? (
        <Box
          ref={videoRef}
          component="video"
          src="/img/landing/nrs-landing-screen.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
          aria-label="Neurorobotics Studio product demo"
          sx={{
            position: "absolute",
            top: "34px",
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "calc(100% - 34px)",
            objectFit: "cover",
          }}
        />
      ) : (
        <NeuralDotGrid />
      )}
    </Box>
  );
}

/** Animated neural dot grid — fallback when video is unavailable. */
function NeuralDotGrid() {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: (theme) =>
          `radial-gradient(ellipse 80% 60% at 50% 50%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 2 }}>
        {Array.from({ length: 60 }).map((_, i) => {
          const activeSet = new Set([4, 9, 13, 18, 22, 28, 33, 37, 42, 47, 51, 56]);
          const brightSet = new Set([18, 28, 42]);
          const isActive = activeSet.has(i);
          const isBright = brightSet.has(i);
          const delay = (i * 0.07) % 2.5;
          return (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: isBright
                  ? "primary.main"
                  : isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.5)
                    : (theme) => alpha(theme.palette.primary.main, 0.1),
                boxShadow: isBright
                  ? (theme) => `0 0 12px ${alpha(theme.palette.primary.main, 0.9)}`
                  : "none",
                animation:
                  isBright || isActive
                    ? `${pulse} ${1.8 + delay * 0.3}s ease-in-out infinite`
                    : "none",
                animationDelay: `${delay}s`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

/** Animated reveal wrapper — slides up when entering viewport; text stays visible for crawlers. */
function Reveal({
  children,
  delay = 0,
  fullHeight = false,
}: {
  children: React.ReactNode;
  delay?: number;
  fullHeight?: boolean;
}) {
  const { ref, inView } = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        height: fullHeight ? "100%" : undefined,
        opacity: 1,
        transform: inView ? "translateY(0)" : "translateY(16px)",
        transition: `transform 0.65s ease ${delay}s`,
        willChange: "transform",
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Feature components
// ---------------------------------------------------------------------------


/**
 * Feature detail dialog — opens on tile click.
 * Shows capabilities list, body text, and CTAs.
 */
function FeatureDetailDialog({
  feat,
  onClose,
}: {
  feat: Feature | null;
  onClose: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  return (
    <Dialog
      open={!!feat}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderRadius: isMobile ? 0 : 2.5,
          overflow: "hidden",
        },
      }}
    >
      {feat && (
        <>
          {/* Header image with title overlay */}
          <Box
            sx={{
              position: "relative",
              height: { xs: 180, sm: 220 },
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={feat.bg}
              alt=""
              aria-hidden
              sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.75) 100%)",
              }}
            />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, p: { xs: 2.5, sm: 3 } }}>
              <Typography
                variant="caption"
                sx={{
                  color: feat.color,
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  display: "block",
                  mb: 0.5,
                }}
              >
                {feat.label}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.25rem", color: "white", lineHeight: 1.2 }}
              >
                {feat.title}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close"
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "white",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack spacing={3}>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.72, fontSize: "0.9375rem" }}>
                {feat.body}
              </Typography>

              {/* Capabilities list */}
              <Box>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.875rem", mb: 1.5, letterSpacing: "-0.01em" }}
                >
                  What you can do
                </Typography>
                <Stack spacing={1.25}>
                  {feat.detail.capabilities.map((cap) => (
                    <Stack key={cap} direction="row" spacing={1.25} alignItems="flex-start">
                      <CheckIcon
                        sx={{ fontSize: "0.95rem", color: feat.color, mt: "3px", flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", lineHeight: 1.65 }}
                      >
                        {cap}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* CTAs */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} pt={0.5} alignItems="stretch">
                <Box
                  component={NextLink}
                  href="/nrs/download"
                  onClick={() => trackDownloadPageCtaClick({ source: "landing_feature_dialog_download_cta" })}
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    borderRadius: "12px",
                    overflow: "hidden",
                    textDecoration: "none",
                    flex: 1,
                    border: "1px solid rgba(144,202,249,0.22)",
                    boxShadow: "0 4px 16px rgba(90,183,254,0.15)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 28px rgba(90,183,254,0.32)",
                      "& .nrs-dialog-btn-label": { background: "rgba(144,202,249,0.18)" },
                    },
                  }}
                >
                  <Box sx={{ width: 48, height: 48, flexShrink: 0, overflow: "hidden" }}>
                    <Image
                      src="/img/nrs-neurorobotics-studio-icon.png"
                      alt="Neurorobotics Studio"
                      width={48}
                      height={48}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                  <Box
                    className="nrs-dialog-btn-label"
                    sx={{
                      flexGrow: 1,
                      px: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      backdropFilter: "blur(14px)",
                      WebkitBackdropFilter: "blur(14px)",
                      background: "rgba(144,202,249,0.10)",
                      borderLeft: "1px solid rgba(144,202,249,0.18)",
                      transition: "background 0.2s ease",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: "0.58rem",
                        fontWeight: 500,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        lineHeight: 1,
                        mb: 0.4,
                      }}
                    >
                      macOS & Windows
                    </Typography>
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        letterSpacing: "-0.01em",
                        lineHeight: 1,
                      }}
                    >
                      Download
                    </Typography>
                  </Box>
                </Box>
                {feat.detail.moreHref !== "/nrs/download" && (
                  <Button
                    component={NextLink}
                    href={feat.detail.moreHref}
                    variant="outlined"
                    fullWidth
                    sx={{
                      fontWeight: 600,
                      borderRadius: 1.5,
                      py: 1.125,
                      borderColor: "divider",
                      color: "text.primary",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    Explore {feat.label}
                  </Button>
                )}
              </Stack>
            </Stack>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

/**
 * Feature tile — full-bleed background image with gradient scrim and cursor glow.
 * Clickable: opens the detail dialog.
 */
function FeatureCard({
  feat,
  delay,
  onClick,
}: {
  feat: Feature;
  delay: number;
  onClick: () => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [glow, setGlow] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    setGlow({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, []);

  return (
    <Reveal delay={delay}>
      <Box
        ref={cardRef}
        role="button"
        tabIndex={0}
        aria-label={`Learn more about ${feat.label}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: "relative",
          height: { xs: 280, sm: 300, md: 320 },
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          border: "1px solid",
          borderColor: hovered
            ? `${feat.color}55`
            : (theme) => alpha(theme.palette.common.white, 0.08),
          transition: "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease",
          transform: hovered ? "translateY(-6px) scale(1.01)" : "translateY(0) scale(1)",
          boxShadow: hovered
            ? `0 24px 70px rgba(0,0,0,0.55), 0 0 0 1px ${feat.color}33`
            : "0 8px 32px rgba(0,0,0,0.35)",
          "&:focus-visible": {
            outline: `2px solid ${feat.color}`,
            outlineOffset: 2,
          },
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src={feat.bg}
          alt=""
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
            transform: hovered ? "scale(1.06)" : "scale(1)",
          }}
        />

        {/* Gradient scrim */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.82) 75%, rgba(0,0,0,0.92) 100%)",
          }}
        />

        {/* Cursor glow overlay */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.35s ease",
            background: `radial-gradient(circle at ${glow.x}% ${glow.y}%, ${feat.color}28 0%, transparent 60%)`,
            pointerEvents: "none",
          }}
        />

        {/* Number badge — top left */}
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            px: 1,
            py: 0.375,
            borderRadius: 1,
            bgcolor: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
            border: `1px solid ${feat.color}44`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.65rem",
              color: feat.color,
              letterSpacing: "0.1em",
              fontFamily: "monospace",
              lineHeight: 1,
            }}
          >
            {feat.number}
          </Typography>
        </Box>

        {/* "Learn more" hint — top right, visible on hover */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.25s ease",
            px: 1,
            py: 0.375,
            borderRadius: 1,
            bgcolor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            border: `1px solid ${feat.color}33`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.6rem",
              color: feat.color,
              fontWeight: 600,
              letterSpacing: "0.08em",
              lineHeight: 1,
            }}
          >
            Learn more
          </Typography>
        </Box>

        {/* Text content — anchored to bottom */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: { xs: 2.5, sm: 3 } }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: feat.color,
              fontWeight: 700,
              fontSize: "0.65rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              mb: 0.75,
              opacity: 0.9,
            }}
          >
            {feat.label}
          </Typography>
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: "1.05rem", md: "1.125rem" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "white",
              lineHeight: 1.25,
              mb: 0.75,
            }}
          >
            {feat.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.72)",
              lineHeight: 1.65,
              fontSize: "0.875rem",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {feat.body}
          </Typography>
        </Box>
      </Box>
    </Reveal>
  );
}

// ---------------------------------------------------------------------------
// Persona section — who is NRS for?
// ---------------------------------------------------------------------------

const PERSONAS = [
  {
    label: "Physical AI & Embodied Intelligence",
    audience: "AI Developers",
    accent: "#90caf9",
    description:
      "Go beyond fine-tuning LLMs on robot data. Design multi-modal neural architectures that perceive, reason, and act in real physical environments, using a brain-inspired engine built for on-device inference.",
    signals: ["Multi-modal perception", "On-device inference", "No GPU cluster required"],
  },
  {
    label: "Cognitive Control for Edge Robots",
    audience: "Robotics Engineers",
    accent: "#f3bd00",
    description:
      "Replace brittle rule-based controllers with adaptive neural systems that learn from demonstration. Native ROS2 integration, built-in simulators, and one-click deployment to your target hardware.",
    signals: ["ROS2 native bridge", "Rapid prototyping", "Edge & mobile robots"],
  },
  {
    label: "Neural Circuit Hypothesis Testing",
    audience: "Cognitive Neuroscientists",
    accent: "#ce93d8",
    description:
      "Implement real cortical circuits, map them to anatomical brain regions, and test your models against behavioral outcomes, all inside a simulated embodiment you can observe in 3D in real time.",
    signals: ["3D connectome visualizer", "Cortical area mapping", "Simulated embodiments"],
  },
  {
    label: "Learn by Building, Not Reading",
    audience: "Hobbyists & Students",
    accent: "#81c784",
    description:
      "No background in AI or robotics required. Pick a simulator, wire up your first neural circuit, and watch it learn, all in the same afternoon. Free community tier. Runs locally after sign-in.",
    signals: ["Zero setup", "Built-in simulators", "Free community tier"],
  },
];

/** Persona section — surfaces the four primary use-case groups. */
function PersonaSection() {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, position: "relative", overflow: "hidden" }}>
      <GradientOrb color="#90caf9" size={400} top="10%" left="5%" delay={3} duration={22} />
      <GradientOrb color="#81c784" size={300} top="60%" left="85%" delay={7} duration={18} />

      <Container maxWidth="lg">
        <Reveal>
          <Box sx={{ mb: { xs: 6, md: 8 }, maxWidth: "42rem" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                display: "block",
                mb: 1,
              }}
            >
              Who is this for?
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.035em",
                lineHeight: 1.2,
              }}
            >
              Built for the people putting AI into the physical world
            </Typography>
          </Box>
        </Reveal>

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {PERSONAS.map((p, i) => (
            <Grid item xs={12} sm={6} key={p.audience} sx={{ display: "flex", flexDirection: "column" }}>
              <Reveal delay={i * 0.08} fullHeight>
                <Box
                  sx={{
                    ...nrsPanelSx,
                    p: { xs: 3, sm: 3.5 },
                    height: "100%",
                    borderLeft: `3px solid ${p.accent}`,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: `0 16px 48px ${alpha("#000", 0.35)}, 0 0 0 1px ${alpha(p.accent, 0.2)}`,
                    },
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: p.accent,
                          mb: 0.5,
                        }}
                      >
                        {p.audience}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem", letterSpacing: "-0.02em" }}>
                        {p.label}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.72 }}>
                      {p.description}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {p.signals.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.625rem",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            bgcolor: alpha(p.accent, 0.1),
                            color: p.accent,
                            border: "none",
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

/** Features section — manages dialog state and renders a responsive grid. */
function FeaturesSection() {
  const [selectedFeat, setSelectedFeat] = useState<Feature | null>(null);

  return (
    <>
      <Grid container spacing={{ xs: 2.5, md: 3 }}>
        {FEATURES.map((feat, i) => (
          <Grid key={feat.number} item xs={12} sm={6} md={4}>
            <FeatureCard feat={feat} delay={i * 0.08} onClick={() => setSelectedFeat(feat)} />
          </Grid>
        ))}
      </Grid>
      <FeatureDetailDialog feat={selectedFeat} onClose={() => setSelectedFeat(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Platform download tile
// ---------------------------------------------------------------------------

/** Platform download tile. */
function PlatformTile({ icon, label, arch }: { icon: string; label: string; arch: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component={NextLink}
      href="/nrs/download"
      onClick={() => trackDownloadPageCtaClick({ source: `landing_platform_tile_${label.toLowerCase().replace(/\s+/g, "_")}` })}
      aria-label={`Download Neurorobotics Studio for ${label}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        ...nrsPanelSx,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        p: { xs: 2.5, sm: 3 },
        minHeight: { xs: 130, sm: 150 },
        textDecoration: "none",
        color: "text.primary",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        borderColor: hovered ? "primary.main" : undefined,
        boxShadow: hovered
          ? (theme) =>
              `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}, 0 20px 50px ${alpha("#000", 0.4)}`
          : undefined,
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        aria-hidden
        sx={{ width: 40, height: 40, objectFit: "contain", display: "block" }}
      />
      <Box sx={{ textAlign: "center" }}>
        <Typography fontWeight={700} sx={{ fontSize: "0.9375rem", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.625rem",
            mt: 0.25,
          }}
        >
          {arch}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "primary.main",
          fontWeight: 600,
          fontSize: "0.8rem",
          animation: hovered ? `${shimmer} 1.2s linear infinite` : "none",
          backgroundImage: hovered
            ? "linear-gradient(90deg, #90caf9 0%, #e3f2fd 50%, #90caf9 100%)"
            : undefined,
          backgroundSize: "200% auto",
          WebkitBackgroundClip: hovered ? "text" : undefined,
          WebkitTextFillColor: hovered ? "transparent" : undefined,
          backgroundClip: hovered ? "text" : undefined,
        }}
      >
        Download latest
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FAQ section — visible Q&A for UX, mirrors FAQPage JSON-LD schema
// ---------------------------------------------------------------------------

/** Collapsible FAQ section. Uses the same entries as the FAQPage JSON-LD schema. */
function FaqSection() {
  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="md">
        <Reveal>
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                display: "block",
                mb: 1,
              }}
            >
              Frequently asked questions
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.035em",
                lineHeight: 1.2,
              }}
            >
              Everything you need to know
            </Typography>
          </Box>
        </Reveal>

        <Stack spacing={1.5}>
          {HOME_FAQS.map((faq, i) => (
            <Reveal key={faq.question} delay={i * 0.04}>
              <Box
                component="details"
                sx={{
                  ...nrsPanelSx,
                  overflow: "hidden",
                  transition: "border-color 0.18s ease",
                  borderColor: "divider",
                  "&[open]": { borderColor: "primary.main" },
                  "&[open] .faq-chevron": { transform: "rotate(45deg)" },
                  "& summary": {
                    listStyle: "none",
                    cursor: "pointer",
                  },
                  "& summary::-webkit-details-marker": {
                    display: "none",
                  },
                }}
              >
                <Box
                  component="summary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    p: { xs: 2, sm: 2.5 },
                    color: "text.primary",
                    textAlign: "left",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", flex: 1 }}>
                    {faq.question}
                  </Typography>
                  <Box
                    className="faq-chevron"
                    sx={{
                      flexShrink: 0,
                      transition: "transform 0.2s ease",
                      color: "text.secondary",
                      fontSize: "1.25rem",
                      lineHeight: 1,
                      fontWeight: 300,
                    }}
                  >
                    +
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    pb: { xs: 2, sm: 2.5 },
                    pt: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.75 }}
                  >
                    {faq.answer}
                  </Typography>
                </Box>
              </Box>
            </Reveal>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Explore section — Brain Hub + Embodiment Explorer teasers
// ---------------------------------------------------------------------------

/** Explore section: surfaces Brain Hub and Embodiment Explorer before the download CTA. */
function ExploreSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.2),
      }}
    >
      <GradientOrb color="#f3bd00" size={400} top="10%" left="-5%" delay={4} duration={21} />
      <GradientOrb color="#ce93d8" size={300} top="50%" left="80%" delay={8} duration={17} />

      <Container maxWidth="lg">
        <Reveal>
          <Box sx={{ mb: { xs: 6, md: 8 }, maxWidth: "42rem" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                display: "block",
                mb: 1,
              }}
            >
              Explore the platform
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.035em",
                lineHeight: 1.2,
              }}
            >
              More to discover before you download
            </Typography>
          </Box>
        </Reveal>

        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          {/* Brain Hub card */}
          <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
            <Reveal fullHeight>
              <Box
                component={NextLink}
                href="/hub"
                sx={{
                  ...nrsPanelSx,
                  display: "block",
                  textDecoration: "none",
                  color: "text.primary",
                  p: { xs: 3, sm: 4 },
                  height: "100%",
                  transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    borderColor: "#ce93d8",
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 60px ${alpha("#000", 0.4)}, 0 0 0 1px ${alpha("#ce93d8", 0.3)}`,
                  },
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "1.125rem", letterSpacing: "-0.02em" }}
                      >
                        Brain Hub
                      </Typography>
                      <Typography
                        sx={{ color: "#ce93d8", fontSize: "0.875rem", fontWeight: 600 }}
                      >
                        Browse &rarr;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#ce93d8",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      AI model catalog
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.72 }}>
                    Browse, download, and share neuromorphic brain architectures built by the FEAGI
                    community, from simple reflex loops to multi-sensory learning systems.
                  </Typography>

                  {/* Mock genome entry previews */}
                  <Stack spacing={1}>
                    {[
                      { name: "Obstacle Avoidance v3", tags: ["sensor", "motor"] },
                      { name: "Visual Tracker", tags: ["vision", "tracking"] },
                      { name: "ROS2 Navigation Brain", tags: ["ros2", "nav"] },
                    ].map((genome) => (
                      <Box
                        key={genome.name}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                            {genome.name}
                          </Typography>
                          <Stack direction="row" spacing={0.75} mt={0.5}>
                            {genome.tags.map((t) => (
                              <Chip
                                key={t}
                                label={t}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: "0.6rem",
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  bgcolor: alpha("#ce93d8", 0.1),
                                  color: "#ce93d8",
                                  border: "none",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                        <Box
                          aria-hidden
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: alpha("#ce93d8", 0.08),
                            border: "1px solid",
                            borderColor: alpha("#ce93d8", 0.2),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "0.55rem", color: "#ce93d8", fontWeight: 700, lineHeight: 1 }}
                          >
                            DL
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Reveal>
          </Grid>

          {/* Embodiment Explorer card */}
          <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
            <Reveal delay={0.1} fullHeight>
              <Box
                component={NextLink}
                href="/embodiments"
                sx={{
                  ...nrsPanelSx,
                  display: "block",
                  textDecoration: "none",
                  color: "text.primary",
                  p: { xs: 3, sm: 4 },
                  height: "100%",
                  transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    borderColor: "#f3bd00",
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 60px ${alpha("#000", 0.4)}, 0 0 0 1px ${alpha("#f3bd00", 0.3)}`,
                  },
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "1.125rem", letterSpacing: "-0.02em" }}
                      >
                        Embodiment Explorer
                      </Typography>
                      <Typography sx={{ color: "#f3bd00", fontSize: "0.875rem", fontWeight: 600 }}>
                        Explore &rarr;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#f3bd00",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      Robot &amp; simulator categories
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.72 }}>
                    See every robot, simulator, and device NRS supports. Start with a simulator on
                    your laptop and migrate to physical hardware when you&apos;re ready.
                  </Typography>

                  {/* Simulator chips */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      Simulated
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                      {SIMULATOR_CHIPS.map((name) => (
                        <Chip
                          key={name}
                          label={name}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: alpha("#f3bd00", 0.1),
                            color: "#f3bd00",
                            border: "1px solid",
                            borderColor: alpha("#f3bd00", 0.3),
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      Physical robots
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {PHYSICAL_CHIPS.map((name) => (
                        <Chip
                          key={name}
                          label={name}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: (theme) => alpha(theme.palette.common.white, 0.05),
                            color: "text.secondary",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function NrsLandingPage() {
  // Hero text entrance — staggered on mount
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const heroAnim = (delay: number) => ({
    opacity: 1,
    transform: mounted ? "translateY(0)" : "translateY(16px)",
    transition: `transform 0.7s ease ${delay}s`,
    willChange: "transform",
  });

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        minWidth: 0,
        bgcolor: "background.default",
        ...nrsCrispTextSx,
      }}
    >
      {/* ================================================================
          HERO
          ================================================================ */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 64px)" },
          display: "flex",
          alignItems: "center",
          pb: { xs: 8, sm: 10, md: 12 },
          pt: { xs: 6, sm: 8, md: 10 },
        }}
      >
        <GradientOrb color="#90caf9" size={600} top="-15%" left="-10%" delay={0} duration={18} />
        <GradientOrb color="#7c4dff" size={400} top="20%" left="60%" delay={3} duration={22} />
        <GradientOrb color="#00bcd4" size={300} top="60%" left="10%" delay={6} duration={16} />

        {/* Subtle dot grid */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(144,202,249,0.08) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            {/* Left: copy */}
            <Grid item xs={12} md={5}>
              <Stack spacing={{ xs: 3, md: 3.5 }}>

                {/* H1 carries the search terms; the display headline below is styling only. */}
                <Box sx={heroAnim(0.1)}>
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: "0.8125rem", md: "0.875rem" },
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#90caf9",
                    }}
                  >
                    {HOME_H1}
                  </Typography>
                </Box>

                {/* Headline */}
                <Box sx={heroAnim(0.15)}>
                  <Typography
                    component="p"
                    sx={{
                      fontSize: { xs: "2.4rem", sm: "3rem", md: "3.5rem", lg: "3.875rem" },
                      fontWeight: 700,
                      lineHeight: 1.06,
                      letterSpacing: "-0.04em",
                      color: "text.primary",
                    }}
                  >
                    Physical Intelligence,
                    <br />
                    <Box
                      component="span"
                      sx={{
                        backgroundImage:
                          "linear-gradient(120deg, #90caf9 0%, #e3f2fd 40%, #4fc3f7 70%, #90caf9 100%)",
                        backgroundSize: "200% auto",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        animation: `${gradientShift} 5s linear infinite`,
                      }}
                    >
                      Unlocked.
                    </Box>
                  </Typography>
                </Box>

                {/* Subheadline */}
                <Box sx={heroAnim(0.25)}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.0625rem", sm: "1.125rem", md: "1.15rem" },
                      lineHeight: 1.68,
                      color: "text.secondary",
                      maxWidth: "34rem",
                    }}
                  >
                    On-device inference, with no GPU cluster required.
                    A professional-grade platform with a learning curve that fits a weekend.
                  </Typography>
                </Box>

                {/* CTAs — gated on actual device type, not viewport width */}
                <Box sx={heroAnim(0.35)}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                    {/* Split icon + frosted-glass CTA button */}
                    <Box
                      component={NextLink}
                      href="/nrs/download"
                      onClick={() => trackDownloadPageCtaClick({ source: "landing_hero_download_cta" })}
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        borderRadius: "14px",
                        overflow: "hidden",
                        textDecoration: "none",
                        border: "1px solid rgba(144,202,249,0.22)",
                        boxShadow: "0 4px 20px rgba(90,183,254,0.18)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 10px 36px rgba(90,183,254,0.35)",
                          "& .nrs-btn-label": {
                            background: "rgba(144,202,249,0.18)",
                          },
                        },
                      }}
                    >
                      {/* App icon */}
                      <Box sx={{ width: 58, height: 58, flexShrink: 0, overflow: "hidden" }}>
                        <Image
                          src="/img/nrs-neurorobotics-studio-icon.png"
                          alt="Neurorobotics Studio"
                          width={58}
                          height={58}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          priority
                        />
                      </Box>
                      {/* Frosted glass label */}
                      <Box
                        className="nrs-btn-label"
                        sx={{
                          px: 2.25,
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          backdropFilter: "blur(14px)",
                          WebkitBackdropFilter: "blur(14px)",
                          background: "rgba(144,202,249,0.10)",
                          borderLeft: "1px solid rgba(144,202,249,0.18)",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.5)",
                            fontSize: "0.6rem",
                            fontWeight: 500,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            lineHeight: 1,
                            mb: 0.45,
                          }}
                        >
                          macOS & Windows
                        </Typography>
                        <Typography
                          sx={{
                            color: "white",
                            fontSize: "0.9375rem",
                            fontWeight: 700,
                            letterSpacing: "-0.01em",
                            lineHeight: 1,
                          }}
                        >
                          Download
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      component={NextLink}
                      href="/pricing"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        height: 58,
                        px: 2.25,
                        borderRadius: "14px",
                        border: "1px solid rgba(255,255,255,0.1)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                        background: "rgba(255,255,255,0.05)",
                        textDecoration: "none",
                        transition: "border-color 0.2s ease, background 0.2s ease",
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.22)",
                          background: "rgba(255,255,255,0.09)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.45)",
                          fontSize: "0.6rem",
                          fontWeight: 500,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          lineHeight: 1,
                          mb: 0.45,
                        }}
                      >
                        Compare tiers
                      </Typography>
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: "0.9375rem",
                          fontWeight: 700,
                          letterSpacing: "-0.01em",
                          lineHeight: 1,
                        }}
                      >
                        View plans
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Platform note */}
                <Box sx={heroAnim(0.42)}>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.78rem" }}
                  >
                    {"Free community tier. macOS (Apple Silicon and Intel) and Windows x64. "}
                    <Link
                      component={NextLink}
                      href="/pricing"
                      color="primary"
                      underline="hover"
                      sx={{ fontSize: "inherit", display: { xs: "none", md: "inline" } }}
                    >
                      Pro and Team plans available.
                    </Link>
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Right: product video */}
            <Grid item xs={12} md={7}>
              <HeroVideo />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* ================================================================
          WHO IS THIS FOR — persona section
          ================================================================ */}
      <PersonaSection />

      <Divider />

      {/* ================================================================
          FEATURES — grid on desktop, scroll-snap carousel on mobile
          ================================================================ */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: "relative", overflow: "hidden" }}>
        <GradientOrb color="#ce93d8" size={500} top="20%" left="70%" delay={2} duration={20} />

        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ mb: { xs: 6, md: 8 }, maxWidth: "42rem" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  display: "block",
                  mb: 1,
                }}
              >
                A few highlights
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.75rem", md: "2.25rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.035em",
                  lineHeight: 1.2,
                  mb: 1.5,
                }}
              >
                Every tool in one workspace
              </Typography>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.65, fontSize: "1rem" }}>
                Stop stitching together tools that don&apos;t talk to each other. The full
                experiment loop (design, train, connect, deploy) in a single desktop app.{" "}
                <Box component="span" sx={{ color: "primary.main", fontWeight: 500 }}>
                  Tap any tile to learn more.
                </Box>
              </Typography>
            </Box>
          </Reveal>

          <FeaturesSection />

          <Reveal>
            <Box sx={{ mt: { xs: 4, md: 5 }, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                These are just a few of the tools inside Neurorobotics Studio.{" "}
                <Link
                  component={NextLink}
                  href="/nrs/download"
                  color="primary"
                  underline="hover"
                  onClick={() => trackDownloadPageCtaClick({ source: "landing_features_footer_link" })}
                >
                  Download and explore the full suite.
                </Link>
              </Typography>
            </Box>
          </Reveal>
        </Container>
      </Box>

      <Divider />

      {/* ================================================================
          HOW IT WORKS
          ================================================================ */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.3),
          position: "relative",
          overflow: "hidden",
        }}
      >
        <GradientOrb color="#90caf9" size={400} top="-20%" left="80%" delay={1} duration={19} />

        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ mb: { xs: 6, md: 8 }, maxWidth: "38rem" }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.75rem", md: "2.25rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.035em",
                  lineHeight: 1.2,
                }}
              >
                From install to experiment
                <br />
                <Box
                  component="span"
                  sx={{
                    backgroundImage: "linear-gradient(120deg, #90caf9 0%, #4fc3f7 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  in minutes.
                </Box>
              </Typography>
            </Box>
          </Reveal>

          <Grid container spacing={{ xs: 4, md: 3 }}>
            {STEPS.map((s, i) => (
              <Grid key={s.step} item xs={12} md={4}>
                <Reveal delay={i * 0.12}>
                  <Stack direction="row" spacing={2.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        border: "1px solid",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        animation: `${pulse} ${2 + i * 0.4}s ease-in-out infinite`,
                        animationDelay: `${i * 0.5}s`,
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "1rem", color: "primary.main" }}
                      >
                        {s.step}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.0625rem",
                          letterSpacing: "-0.02em",
                          mb: 0.75,
                        }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", lineHeight: 1.72 }}
                      >
                        {s.body}
                      </Typography>
                    </Box>
                  </Stack>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* ================================================================
          EXPLORE — Brain Hub + Embodiment Explorer teasers
          ================================================================ */}
      <ExploreSection />

      <Divider />

      {/* ================================================================
          DOWNLOAD
          ================================================================ */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Reveal>
            <Stack spacing={{ xs: 4, md: 5 }} alignItems="center">
              <Box sx={{ textAlign: "center", maxWidth: "36rem" }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    fontWeight: 700,
                    letterSpacing: "-0.035em",
                    lineHeight: 1.2,
                    mb: 1.5,
                  }}
                >
                  Download Neurorobotics Studio
                </Typography>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                  Free community tier. Runs locally. Your models, your data. No hardware required
                  to get started.
                </Typography>
              </Box>

              <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  sx={{ maxWidth: 720, width: "100%" }}
                >
                  <Grid item xs={12} sm={4}>
                    <PlatformTile
                      icon="/img/nrs/happy-mac.svg"
                      label="macOS Apple Silicon"
                      arch="arm64 (M Series)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <PlatformTile
                      icon="/img/nrs/happy-mac.svg"
                      label="macOS Intel"
                      arch="x86_64"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <PlatformTile
                      icon="/img/nrs/application.svg"
                      label="Windows"
                      arch="x86_64 (Win 10 / 11)"
                    />
                  </Grid>
                </Grid>
            </Stack>
          </Reveal>
        </Container>
      </Box>

      <Divider />

      {/* ================================================================
          ECOSYSTEM
          ================================================================ */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.25),
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Reveal>
                <Stack spacing={2.5}>
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      lineHeight: 1.25,
                    }}
                  >
                    Part of the Brains for Robots
                    <Box
                      component="sup"
                      sx={{ fontSize: "0.45em", verticalAlign: "super", ml: "1px" }}
                    >
                      ®
                    </Box>{" "}
                    ecosystem
                  </Typography>
                  <Typography sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                    Neurorobotics Studio is the desktop hub for the broader platform. Browse robot
                    embodiments, share brain models with the community, and run experiments, all
                    connected through your local workspace.
                  </Typography>
                </Stack>
              </Reveal>
            </Grid>

            <Grid item xs={12} md={6}>
              <Reveal delay={0.1}>
                <Box
                  sx={{
                    ...nrsPanelSx,
                    p: { xs: 3, sm: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {[
                    {
                      name: "Neurorobotics Studio",
                      role: "Desktop workbench",
                      href: "/nrs/download",
                      accent: "#90caf9",
                      current: true,
                      external: false,
                      externalLabel: "",
                    },
                    {
                      name: "Brain Hub",
                      role: "AI model catalog",
                      href: "/hub",
                      accent: "#ce93d8",
                      current: false,
                      external: false,
                      externalLabel: "",
                    },
                    {
                      name: "Embodiment Explorer",
                      role: "Robot & simulator browser",
                      href: "/embodiments",
                      accent: "#f3bd00",
                      current: false,
                      external: false,
                      externalLabel: "",
                    },
                    {
                      name: "FEAGI",
                      role: "Open-source neuromorphic engine",
                      href: "/feagi",
                      accent: "#81c784",
                      current: false,
                      external: false,
                      externalLabel: "",
                    },
                  ].map((item) => (
                    <Box
                      key={item.name}
                      component={item.href.startsWith("http") ? "a" : NextLink}
                      href={item.href}
                      {...(item.href.startsWith("http")
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.75,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: item.current
                          ? alpha(item.accent, 0.4)
                          : "divider",
                        bgcolor: item.current ? alpha(item.accent, 0.06) : "transparent",
                        textDecoration: "none",
                        color: "text.primary",
                        cursor: "pointer",
                        transition: "border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
                        "&:hover": {
                          borderColor: item.accent,
                          bgcolor: alpha(item.accent, 0.08),
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: item.accent,
                          flexShrink: 0,
                          opacity: item.current ? 1 : 0.45,
                          boxShadow: item.current ? `0 0 6px ${item.accent}` : "none",
                          transition: "opacity 0.18s ease",
                          "a:hover > &, div:hover > &": { opacity: 1 },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9375rem",
                            color: item.current ? item.accent : "text.primary",
                          }}
                        >
                          {item.name}
                          {item.current && (
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: item.accent,
                                opacity: 0.7,
                              }}
                            >
                              you are here
                            </Box>
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          {item.role}
                        </Typography>
                      </Box>
                      {item.external ? (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              color: "text.disabled",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 0.75,
                              px: 0.75,
                              py: 0.25,
                              lineHeight: 1.4,
                            }}
                          >
                            {item.externalLabel}
                          </Box>
                          <OpenInNewIcon sx={{ fontSize: "0.9rem", color: "text.disabled" }} />
                        </Stack>
                      ) : (
                        <ChevronRightIcon
                          sx={{ fontSize: "1.1rem", color: "text.disabled", flexShrink: 0 }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================================================================
          FAQ
          ================================================================ */}
      <FaqSection />

      <Divider />

      {/* ================================================================
          BOTTOM CTA
          ================================================================ */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <GradientOrb color="#90caf9" size={700} top="0%" left="20%" delay={0} duration={20} />
        <GradientOrb color="#7c4dff" size={400} top="40%" left="60%" delay={5} duration={15} />

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Reveal>
            <Stack spacing={3} alignItems="center">
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.75rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 1.1,
                }}
              >
                Ready to build your first brain?
              </Typography>
              <Typography
                sx={{ color: "text.secondary", lineHeight: 1.65, maxWidth: "28rem" }}
              >
                {"Download Neurorobotics Studio and start your first experiment today. Free community tier. No robot hardware required."}
              </Typography>
              {/* Split icon + frosted-glass CTA — same pattern as hero for visual bookend */}
              <Box
                component={NextLink}
                href="/nrs/download"
                onClick={() => trackDownloadPageCtaClick({ source: "landing_bottom_download_cta" })}
                sx={{
                  display: "inline-flex",
                  alignItems: "stretch",
                  borderRadius: "14px",
                  overflow: "hidden",
                  textDecoration: "none",
                  border: "1px solid rgba(144,202,249,0.22)",
                  boxShadow: "0 4px 24px rgba(90,183,254,0.22)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 40px rgba(90,183,254,0.38)",
                    "& .nrs-cta-label": { background: "rgba(144,202,249,0.18)" },
                  },
                }}
              >
                <Box sx={{ width: 62, height: 62, flexShrink: 0, overflow: "hidden" }}>
                  <Image
                    src="/img/nrs-neurorobotics-studio-icon.png"
                    alt="Neurorobotics Studio"
                    width={62}
                    height={62}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
                <Box
                  className="nrs-cta-label"
                  sx={{
                    px: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    background: "rgba(144,202,249,0.10)",
                    borderLeft: "1px solid rgba(144,202,249,0.18)",
                    transition: "background 0.2s ease",
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      fontSize: "0.6rem",
                      fontWeight: 500,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      lineHeight: 1,
                      mb: 0.45,
                    }}
                  >
                    macOS & Windows
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      lineHeight: 1,
                    }}
                  >
                    Download
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
}
